import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  Activity, ChevronRight, Clock3, Cog, Cpu, FileText, Filter, FolderKanban, KeyRound, MousePointerClick,
  Rocket, Server, ShieldCheck, Smartphone, TestTube, Upload, UserRound, Users, X,
} from 'lucide-react';

import { loadAudit, loadAuditActions, loadAuditSummary } from './api';
import { Empty, Failed, Head, Loading, Pill, SearchField } from './parts';
import { tx } from './i18n';
import { auditActionLabel } from './labels';
import { errorMessage } from './ui';
import { countryName } from './countries';
import { useLiveResource } from './useLive';
import {
  CHART, COMPACT, ChartTip, FilterMenu, Flag, KpiRow, NUMBER, Panel, RangeTabs, Share, ago, counted, hourly,
  initials, percent, pointLabel, stackRows, tipPoint, useNow, versusAverage,
} from './MobileParts';
import './audit.css';

// Every request the platform accepted or refused, and everything it did on its
// own, drawn with the Mobile app section's parts: what a range adds up to, who and
// what it was, and each event as it was recorded.
//
// Read-only by construction: there is no endpoint that edits or deletes a row,
// which is the only property that makes a log worth keeping. The totals, the chart
// and the rankings are counted by the server over the whole range; the list under
// them is paged, and its search only narrows what has been read, as it says.

const DAY = 86400000;
const PAGE = 200;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const EVERY_UUID = new RegExp(UUID, 'gi');
const HAS_UUID = new RegExp(UUID, 'i');

// A mark for each family of actions, by the word an action's code starts with.
const FAMILY_ICONS = {
  UPLOAD: Upload,
  JOB: Cpu,
  SCHEDULER: Clock3,
  NODE: Server,
  REPORT: FileText,
  AUTH: KeyRound,
  PAGE: MousePointerClick,
  POST: MousePointerClick,
  STAFF: Users,
  ACCOUNT: Users,
  PROJECT: FolderKanban,
  SAMPLE: TestTube,
  RELEASE: Rocket,
  MOBILE: Smartphone,
};

function actionIcon(code) {
  return FAMILY_ICONS[String(code || '').split('_')[0]] || Activity;
}

// Both mirror a column enum exactly, so a lookup cannot miss:
//   actor_type enum('user','node','system')
const ACTORS = {
  user: { icon: UserRound, labelKey: 'ui.audit.actor_person' },
  node: { icon: Server, labelKey: 'ui.audit.actor_worker' },
  system: { icon: Cog, labelKey: 'ui.audit.actor_system' },
};

// Identifiers stay out of the page: a path shows its shape, never the id in it.
function pathOf(event) {
  return event.request_path ? event.request_path.replace(EVERY_UUID, '…') : null;
}

function actionName(event) {
  return auditActionLabel(event.action_code, event.action_label);
}

function who(event) {
  if (event.actor_type === 'user') return event.account_email || tx('ui.audit.unknown_person');
  if (event.actor_type === 'node') return event.node_hostname || tx('ui.audit.actor_worker');
  return tx('ui.audit.actor_system');
}

function subjectOf(event) {
  const subject = event.metadata && event.metadata.subject;
  return subject && subject.label && !HAS_UUID.test(subject.label) ? subject.label : null;
}

function clock(at) {
  return new Date(Number(at)).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fullTime(at) {
  return new Date(Number(at)).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'medium' });
}

function dayHeading(at, now) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  if (at >= start.getTime()) return tx('ui.mobile.today');
  if (at >= start.getTime() - DAY) return tx('ui.mobile.yesterday');
  return new Date(at).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
function bytes(value) {
  let size = Number(value);
  let index = 0;
  while (size >= 1000 && index < BYTE_UNITS.length - 1) { size /= 1000; index += 1; }
  return new Intl.NumberFormat(undefined, {
    style: 'unit', unit: BYTE_UNITS[index], unitDisplay: 'short', maximumFractionDigits: index > 0 && size < 10 ? 1 : 0,
  }).format(size);
}

// A recorded fact as a person reads it. One that is only an identifier is left out.
function factValue(fact) {
  const { key, value } = fact;
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return tx(value ? 'ui.audit.yes' : 'ui.audit.no');
  const number = typeof value === 'number' ? value : (/^\d+(\.\d+)?$/.test(String(value)) ? Number(value) : null);
  if (number !== null && !String(key).endsWith('_id')) return String(key).endsWith('_bytes') ? bytes(number) : NUMBER.format(number);
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  return HAS_UUID.test(text) ? null : text;
}

function Avatar({ event }) {
  if (event.actor_type === 'user') {
    return <span className="xo-audit-avatar is-person">{initials(null, event.account_email)}</span>;
  }
  const Icon = ACTORS[event.actor_type].icon;
  return <span className={`xo-audit-avatar is-${event.actor_type}`}><Icon aria-hidden="true" /></span>;
}

function Outcome({ event }) {
  if (event.outcome === 'failed') {
    return <Pill tone="bad">{event.status_code ? tx('ui.audit.refused_status', { status: event.status_code }) : tx('ui.audit.refused')}</Pill>;
  }
  return <span className="xo-audit-status">{event.status_code || ''}</span>;
}

// ---------------------------------------------------------------- the range

function ActivityChart({ summary }) {
  const data = useMemo(() => summary.series.map((point) => ({ ...point, accepted: point.events - point.failed })), [summary.series]);
  const average = summary.series.length > 0 ? summary.totals.events / summary.series.length : 0;
  const parts = [
    { key: 'accepted', name: tx('ui.audit.accepted'), color: CHART.ink },
    { key: 'failed', name: tx('ui.audit.refused'), color: CHART.failed },
  ];
  if (summary.totals.events === 0) return <Empty title={tx('ui.audit.nothing_recorded')} />;
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(summary.step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={40} tickFormatter={(value) => COMPACT.format(value)} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
            <ChartTip
              title={tipPoint(summary.step)}
              rows={(point) => stackRows(point, parts)}
              facts={(point) => [
                { label: tx('ui.mobile.tip_share_of_range'), value: point.events > 0 ? percent(point.events, summary.totals.events) : null },
                point.events > 0 ? versusAverage(point.events, average) : { note: tx('ui.audit.tip_quiet') },
              ]}
            />
          )} />
          <Bar dataKey="accepted" stackId="events" fill={CHART.ink} maxBarSize={22} name={tx('ui.audit.accepted')} />
          <Bar dataKey="failed" stackId="events" fill={CHART.failed} radius={[3, 3, 0, 0]} maxBarSize={22} name={tx('ui.audit.refused')} />
        </BarChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">
        <span><i style={{ background: CHART.ink }} />{tx('ui.audit.accepted')}</span>
        <span><i style={{ background: CHART.failed }} />{tx('ui.audit.refused')}</span>
      </div>
    </div>
  );
}

// The refusals and failures a range had most of, by action and answer.
function Refusals({ summary, outcome, now, onPick }) {
  if (outcome === 'ok') return <Empty title={tx('ui.audit.accepted_only')} />;
  if (summary.refusals.length === 0) {
    return (
      <div className="xo-audit-clear">
        <ShieldCheck aria-hidden="true" />
        <strong>{tx('ui.audit.nothing_refused')}</strong>
        <span>{tx('ui.audit.nothing_refused_detail')}</span>
      </div>
    );
  }
  return (
    <ol className="xo-audit-refusals">
      {summary.refusals.map((row) => {
        const Icon = actionIcon(row.action_code);
        return (
          <li key={`${row.action_code}-${row.status_code}`}>
            <button type="button" onClick={() => onPick(row.action_code)}>
              <span className="xo-audit-refusal-icon"><Icon aria-hidden="true" /></span>
              <span className="xo-audit-line">
                <strong>{auditActionLabel(row.action_code, row.action_label)}</strong>
                <small>{tx('ui.audit.latest_value', { when: ago(row.latest, now) })}</small>
              </span>
              <span className="xo-audit-code">{row.status_code || tx('ui.audit.no_answer')}</span>
              <em>{NUMBER.format(row.events)}</em>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function TopActions({ summary, picked, onPick }) {
  if (summary.actions.length === 0) return <Empty title={tx('ui.audit.nothing_recorded')} />;
  const most = Math.max(1, ...summary.actions.map((row) => row.events));
  return (
    <ol className="xo-audit-ranked">
      {summary.actions.map((row) => {
        const Icon = actionIcon(row.action_code);
        const active = picked === row.action_code;
        return (
          <li key={row.action_code}>
            <button type="button" className={active ? 'is-active' : ''} aria-pressed={active}
              onClick={() => onPick(active ? '' : row.action_code)}>
              <span className="xo-mob-rank-icon"><Icon aria-hidden="true" /></span>
              <span className="xo-audit-line">
                <strong>{auditActionLabel(row.action_code, row.action_label)}</strong>
                <small>
                  {percent(row.events, summary.totals.events)}
                  {row.failed > 0 && <b>{tx('ui.audit.refused_count', { count: NUMBER.format(row.failed) })}</b>}
                </small>
              </span>
              <Share value={row.events} total={most} />
              <em>{COMPACT.format(row.events)}</em>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function TopPeople({ summary, picked, now, onPick }) {
  if (summary.people.length === 0) return <Empty title={tx('ui.audit.nobody_acted')} />;
  return (
    <ol className="xo-audit-ranked is-people">
      {summary.people.map((row) => {
        const active = picked === row.account_id;
        return (
          <li key={row.account_id}>
            <button type="button" className={active ? 'is-active' : ''} aria-pressed={active}
              onClick={() => onPick(active ? null : row.account_id)}>
              <span className="xo-audit-avatar is-person">{initials(null, row.email)}</span>
              <span className="xo-audit-line">
                <strong>{row.email}</strong>
                <small>
                  {tx('ui.audit.latest_value', { when: ago(row.latest, now) })}
                  {row.failed > 0 && <b>{tx('ui.audit.refused_count', { count: NUMBER.format(row.failed) })}</b>}
                </small>
              </span>
              <em>{COMPACT.format(row.events)}</em>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function WorkersAndPlaces({ summary, now }) {
  const most = Math.max(1, ...summary.countries.map((row) => row.events));
  return (
    <>
      <h3 className="xo-mob-subhead">{tx('ui.audit.workers')}</h3>
      {summary.nodes.length === 0 ? <p className="xo-audit-quiet">{tx('ui.audit.no_workers')}</p> : (
        <ol className="xo-audit-ranked is-static">
          {summary.nodes.map((row) => (
            <li key={row.node_id}>
              <span className="xo-audit-row">
                <span className="xo-audit-avatar is-node"><Server aria-hidden="true" /></span>
                <span className="xo-audit-line">
                  <strong>{row.hostname}</strong>
                  <small>{tx('ui.audit.latest_value', { when: ago(row.latest, now) })}</small>
                </span>
                <em>{COMPACT.format(row.events)}</em>
              </span>
            </li>
          ))}
        </ol>
      )}
      <h3 className="xo-mob-subhead">{tx('ui.audit.places')}</h3>
      {summary.countries.length === 0 ? <p className="xo-audit-quiet">{tx('ui.audit.no_places')}</p> : (
        <ol className="xo-mob-places">
          {summary.countries.map((row) => (
            <li key={row.country}>
              <Flag code={row.country} />
              <span><strong>{countryName(row.country)}</strong><small>{percent(row.events, summary.totals.events)}</small></span>
              <Share value={row.events} total={most} />
              <em className="xo-audit-count">{COMPACT.format(row.events)}</em>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

// ---------------------------------------------------------------- the events

function Feed({ events, now, onOpen }) {
  const days = useMemo(() => {
    const groups = [];
    for (const event of events) {
      const heading = dayHeading(Number(event.created_at), now);
      const last = groups[groups.length - 1];
      if (last && last.heading === heading) last.events.push(event);
      else groups.push({ heading, events: [event] });
    }
    return groups;
  }, [events, now]);
  return (
    <div className="xo-audit-feed">
      {days.map((group) => (
        <section key={group.heading} className="xo-audit-day">
          <h3>{group.heading}<span>{counted('ui.audit.events_count', group.events.length)}</span></h3>
          <ol>
            {group.events.map((event) => {
              const subject = subjectOf(event);
              const path = pathOf(event);
              return (
                <li key={event.id}>
                  <button type="button" className={event.outcome === 'failed' ? 'is-failed' : ''} onClick={() => onOpen(event)}>
                    <time>{clock(event.created_at)}</time>
                    <Avatar event={event} />
                    <span className="xo-audit-line">
                      <strong><b>{who(event)}</b>{actionName(event)}</strong>
                      <small>
                        {subject && <span className="xo-audit-subject">{subject}</span>}
                        {path && <code>{event.http_method} {path}</code>}
                      </small>
                    </span>
                    <span className="xo-audit-where">
                      {event.client_ip && <><Flag code={event.cf_country_code} /><code>{event.client_ip}</code></>}
                    </span>
                    <Outcome event={event} />
                    <ChevronRight aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

function Fields({ title, rows, mono = false }) {
  const shown = rows.filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (shown.length === 0) return null;
  return (
    <section>
      <h3 className="xo-mob-subhead">{title}</h3>
      <dl className={`xo-audit-fields${mono ? ' is-mono' : ''}`}>
        {shown.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl>
    </section>
  );
}

// Everything one event recorded: how it ended, who did it and from where, the
// request it came from, and what it touched.
function EventDetail({ event, now, onClose }) {
  useEffect(() => {
    const close = (key) => { if (key.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  const Icon = actionIcon(event.action_code);
  const metadata = event.metadata || {};
  const facts = (Array.isArray(metadata.facts) ? metadata.facts : [])
    .map((fact) => [fact.label || fact.key, factValue(fact)]);
  const failed = event.outcome === 'failed';
  const actor = ACTORS[event.actor_type];
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(click) => { if (click.target === click.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide xo-audit-event" role="dialog" aria-modal="true" aria-labelledby="xo-audit-event-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className={`xo-mob-dialog-icon${failed ? ' is-failed' : ''}`}><Icon aria-hidden="true" /></span>
          <div>
            <h2 id="xo-audit-event-title">{actionName(event)}</h2>
            <p>{fullTime(event.created_at)}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.audit.close')} onClick={onClose}><X /></button>
        </header>
        <div className="xo-mob-recipient-facts">
          <span>
            <small>{tx('ui.audit.outcome_d3f06106')}</small>
            <strong className={failed ? 'is-bad' : 'is-ok'}>{tx(failed ? 'ui.audit.refused' : 'ui.audit.accepted')}</strong>
          </span>
          <span><small>{tx('ui.audit.who_7d531617')}</small><strong className="xo-audit-fact-who"><Avatar event={event} />{tx(actor.labelKey)}</strong></span>
          <span><small>{tx('ui.audit.when_769bb19e')}</small><strong>{ago(Number(event.created_at), now)}</strong></span>
          <span>
            <small>{tx('ui.audit.where_from_e6ad44f1')}</small>
            <strong className="xo-audit-fact-place">
              {event.cf_country_code ? <><Flag code={event.cf_country_code} />{countryName(event.cf_country_code)}</> : '—'}
            </strong>
          </span>
        </div>
        <div className="xo-audit-event-body">
          <Fields title={tx('ui.audit.by')} rows={[
            [tx('ui.audit.who_7d531617'), who(event)],
            [tx('ui.audit.address'), event.client_ip],
          ]} />
          <Fields title={tx('ui.audit.request')} mono rows={[
            [tx('ui.audit.method'), event.http_method],
            [tx('ui.audit.path'), pathOf(event)],
            [tx('ui.audit.answer'), event.status_code],
            [tx('ui.audit.event_number'), `#${event.id}`],
          ]} />
          <Fields title={tx('ui.audit.touched')} rows={[
            [tx('ui.audit.touched_name'), subjectOf(event)],
            [tx('ui.audit.touched_kind'), event.entity_type],
          ]} />
          <Fields title={tx('ui.audit.details')} rows={[
            ...facts,
            [tx('ui.audit.changed'), Array.isArray(metadata.changed) && metadata.changed.length > 0 ? metadata.changed.join(', ') : null],
            [tx('ui.audit.reason'), metadata.reason ? String(metadata.reason).replace(/_/g, ' ') : null],
          ]} />
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------- the page

export default function XomicsAudit() {
  const [days, setDays] = useState(7);
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [accountId, setAccountId] = useState(null);
  const [term, setTerm] = useState('');
  const [opened, setOpened] = useState(null);
  const now = useNow(30000);

  const filters = useMemo(() => ({ action, actorType, outcome, accountId }), [action, actorType, outcome, accountId]);
  const key = JSON.stringify({ ...filters, days });
  // Older pages, kept for the filters they were read under.
  const [older, setOlder] = useState({ key: '', events: [], nextBeforeId: null });
  const [reading, setReading] = useState(false);
  const [olderError, setOlderError] = useState('');

  const loadSummary = useCallback(() => loadAuditSummary({ ...filters, since: Date.now() - days * DAY }), [filters, days]);
  const loadFirst = useCallback(() => loadAudit({ ...filters, since: Date.now() - days * DAY, limit: PAGE }), [filters, days]);
  const loadVocabulary = useCallback(() => loadAuditActions(), []);
  const summary = useLiveResource([], loadSummary, { reconcileMs: 60000 });
  const first = useLiveResource(['audit'], loadFirst, { reconcileMs: 30000 });
  const vocabulary = useLiveResource([], loadVocabulary, { reconcileMs: 0 });

  const kept = older.key === key ? older : { key, events: [], nextBeforeId: null };
  const events = useMemo(() => {
    const byId = new Map();
    for (const event of [...(first.data ? first.data.events : []), ...kept.events]) byId.set(event.id, event);
    return [...byId.values()].sort((a, b) => b.id - a.id);
  }, [first.data, kept.events]);
  const nextBeforeId = kept.events.length > 0 ? kept.nextBeforeId : (first.data ? first.data.nextBeforeId : null);

  // The search narrows what has been read, and the footer says so: a count that
  // looked like the whole log would be a lie about a paged read.
  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (needle === '') return events;
    return events.filter((event) => [actionName(event), who(event), pathOf(event), subjectOf(event), event.entity_type,
      event.client_ip, event.cf_country_code ? countryName(event.cf_country_code) : null]
      .some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(needle)));
  }, [events, term]);

  async function readOlder() {
    setReading(true);
    setOlderError('');
    try {
      const page = await loadAudit({ ...filters, since: Date.now() - days * DAY, limit: PAGE, beforeId: nextBeforeId });
      setOlder({ key, events: [...kept.events, ...page.events], nextBeforeId: page.nextBeforeId });
    } catch (caught) {
      setOlderError(errorMessage(caught));
    } finally {
      setReading(false);
    }
  }

  if ([summary, first].some((resource) => resource.loading && resource.data === null)) {
    return <Loading label={tx('ui.audit.reading_the_log_f2470c05')} />;
  }
  const broken = [summary, first].find((resource) => resource.error && resource.data === null);
  if (broken) return <Failed message={broken.error} onRetry={broken.refresh} />;

  const range = summary.data;
  const totals = range.totals;
  const perPoint = range.series.length > 0 ? totals.events / range.series.length : 0;
  const warning = summary.error || first.error || olderError;
  const labels = vocabulary.data ? vocabulary.data.labels || {} : {};
  const seen = new Map(range.actions.map((row) => [row.action_code, row.events]));
  const actionOptions = [
    { value: '', label: tx('ui.audit.every_action') },
    ...Object.entries(labels)
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([code, label]) => ({ value: code, label, count: action === '' ? seen.get(code) : undefined })),
  ];
  if (action && !labels[action]) actionOptions.push({ value: action, label: auditActionLabel(action, null) });
  const person = accountId === null ? null : range.people.find((row) => row.account_id === accountId);

  return (
    <div className="xo-platform-page xo-web-page xo-audit-page">
      <Head title={tx('ui.audit.audit_log_3cfc5f1c')} subtitle={tx('ui.audit.subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {warning && <p className="xo-alert t-warn" role="alert">{warning}</p>}

      <div className="xo-audit-filters">
        <FilterMenu icon={Filter} label={tx('ui.audit.action_filter')} value={action} options={actionOptions} onChange={setAction} />
        <FilterMenu icon={Users} label={tx('ui.audit.who_filter')} value={actorType} onChange={setActorType} options={[
          { value: '', label: tx('ui.audit.anyone') },
          { value: 'user', label: tx('ui.audit.people') },
          { value: 'node', label: tx('ui.audit.workers') },
          { value: 'system', label: tx('ui.audit.actor_system') },
        ]} />
        <FilterMenu icon={ShieldCheck} label={tx('ui.audit.outcome_d3f06106')} value={outcome} onChange={setOutcome} options={[
          { value: '', label: tx('ui.audit.any_outcome') },
          { value: 'ok', label: tx('ui.audit.accepted') },
          { value: 'failed', label: tx('ui.audit.refused') },
        ]} />
        {accountId !== null && (
          <button type="button" className="xo-audit-chip" onClick={() => setAccountId(null)}>
            <span>{tx('ui.audit.only_person', { email: person ? person.email : `#${accountId}` })}</span><X aria-label={tx('ui.audit.clear')} />
          </button>
        )}
      </div>

      <KpiRow items={[
        { label: tx('ui.audit.events'), value: COMPACT.format(totals.events),
          detail: tx(hourly(range.step) ? 'ui.audit.per_hour_average' : 'ui.audit.per_day_average', { count: NUMBER.format(Math.round(perPoint)) }),
          trend: range.series.map((point) => point.events) },
        { label: tx('ui.audit.people'), value: NUMBER.format(totals.people), detail: tx('ui.audit.people_detail') },
        { label: tx('ui.audit.refused'), value: COMPACT.format(totals.failed),
          detail: tx('ui.audit.refused_share', { share: percent(totals.failed, totals.events) }),
          trend: range.series.map((point) => point.failed) },
        { label: tx('ui.audit.automatic'), value: COMPACT.format(totals.system_events),
          detail: counted('ui.audit.workers_count', totals.nodes) },
      ]} />

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.audit.activity')} detail={tx(hourly(range.step) ? 'ui.audit.activity_detail_hour' : 'ui.audit.activity_detail_day')}>
          <ActivityChart summary={range} />
        </Panel>
        <Panel title={tx('ui.audit.refusals')} detail={tx('ui.audit.refusals_detail')} className="is-refusals">
          <Refusals summary={range} outcome={outcome} now={now} onPick={(code) => { setAction(code); setOutcome('failed'); }} />
        </Panel>
      </div>

      <div className="xo-mob-row is-thirds is-even">
        <Panel title={tx('ui.audit.top_actions')} detail={tx('ui.audit.top_actions_detail')}>
          <TopActions summary={range} picked={action} onPick={setAction} />
        </Panel>
        <Panel title={tx('ui.audit.top_people')} detail={tx('ui.audit.top_people_detail')}>
          <TopPeople summary={range} picked={accountId} now={now} onPick={setAccountId} />
        </Panel>
        <Panel title={tx('ui.audit.workers_places')} detail={tx('ui.audit.workers_places_detail')}>
          <WorkersAndPlaces summary={range} now={now} />
        </Panel>
      </div>

      <Panel flush className="xo-audit-events" title={tx('ui.audit.events')}
        detail={counted('ui.audit.range_events', totals.events)}
        aside={(
          <SearchField value={term} onChange={setTerm} className="xo-audit-search"
            placeholder={tx('ui.audit.filter_by_person_action_or_path_ca6b94ac')}
            label={tx('ui.audit.filter_the_audit_log_1341cb4d')} />
        )}>
        {shown.length === 0
          ? <Empty title={tx(events.length === 0 ? 'ui.audit.nothing_recorded' : 'ui.audit.no_match')} />
          : <Feed events={shown} now={now} onOpen={setOpened} />}
        <footer className="xo-audit-foot">
          <span>{tx('ui.audit.showing_length_of_length2_loaded_3838e392', { length: NUMBER.format(shown.length), length2: NUMBER.format(events.length) })}</span>
          {nextBeforeId !== null && (
            <button type="button" className="xo-mob-more" disabled={reading} onClick={readOlder}>
              {reading ? tx('ui.audit.loading_33ce4174') : tx('ui.audit.load_older')}
            </button>
          )}
        </footer>
      </Panel>

      <AnimatePresence>
        {opened && <EventDetail key={opened.id} event={opened} now={now} onClose={() => setOpened(null)} />}
      </AnimatePresence>
    </div>
  );
}
